import {
  Box,
  CircularProgress,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { format } from "date-fns";
import DialogComponent from "../../../common/components/functional/dialogs/DialogComponent";
import DialogContentWrapper from "../../../common/components/functional/dialogs/DialogContentWrapper";
import {
  getRedactionPlanByTalentId,
  RedactionPlan,
  RedactionPlanStage,
} from "../settingsApi";
import {
  dateTimeFormat,
  fromIsoDateString,
  leadingZerosDateFormat,
} from "../../../common/helpers/formatDateTime";

type Props = {
  isOpen: boolean;
  talentId: string;
  onClose: () => void;
};

export const RedactionPlanDialog = ({ isOpen, talentId, onClose }: Props) => {
  const { t } = useTranslation();
  const [plan, setPlan] = useState<RedactionPlan | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeStep, setActiveStep] = useState(0);

  const getStageLabel = (stage: RedactionPlanStage) =>
    [
      t(`settings:redactionPlanStage.${stage.type}`),
      " (",
      t("settings:redactionPlanStageStartAfter"),
      " ",
      formatIsoDate(stage.startAfterDate),
      ")",
    ].join("");

  const formatIsoDate = (value?: string) =>
    value ? fromIsoDateString(value, leadingZerosDateFormat) : null;

  const formatEpochDateTime = (value?: number) =>
    value ? format(new Date(value), dateTimeFormat) : null;

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    let isActive = true;
    setIsLoading(true);
    setError(null);

    getRedactionPlanByTalentId(talentId)
      .then((response) => {
        if (!isActive) {
          return;
        }
        setPlan(response.data);

        // Array.findLastIndex is unfortunately not available in our current ts target version
        // const index = response.data.stages.findLastIndex((x) => x.startedAt);
        const index = response.data.stages.reduce(
          (a, x, index) => (x.startedAt ? index : a),
          -1,
        );

        if (index !== -1) {
          setActiveStep(index);
        }
      })
      .catch(() => {
        if (!isActive) {
          return;
        }
        setError(t("common:error"));
      })
      .finally(() => {
        if (!isActive) {
          return;
        }
        setIsLoading(false);
      });

    return () => {
      isActive = false;
    };
  }, [isOpen, talentId, t]);

  return (
    <DialogComponent
      open={isOpen}
      onClose={onClose}
      autoWidth
      sx={{ width: "400px" }}
    >
      <DialogContentWrapper
        title={t("settings:redactionPlan")}
        onCancel={onClose}
        customCancelButtonLabel={t("common:close")}
      >
        <Box sx={{ width: "100%", py: 2, px: 2 }}>
          <Typography variant="body2">{talentId}</Typography>
          {isLoading && (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress size={28} />
            </Box>
          )}
          {!isLoading && error && (
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          )}
          {!isLoading && !error && plan?.stages?.length ? (
            <Box mt={3}>
              <Stepper orientation="vertical" activeStep={activeStep}>
                {plan.stages.map((stage) => {
                  const stepProps = stage.endedAt
                    ? { expanded: true, completed: true }
                    : {};

                  return (
                    <Step
                      key={`${stage.type}-${stage.startAfterDate ?? ""}`}
                      {...stepProps}
                    >
                      <StepLabel>{getStageLabel(stage)}</StepLabel>
                      <StepContent>
                        {stage.startedAt && (
                          <Typography variant="caption" display="block">
                            {t("settings:redactionPlanStageStartedAt", {
                              defaultValue: "Started at",
                            })}{" "}
                            {formatEpochDateTime(stage.startedAt)}
                          </Typography>
                        )}
                        {stage.updatedAt && (
                          <Typography variant="caption" display="block">
                            {t("settings:redactionPlanStageUpdatedAt", {
                              defaultValue: "Updated at",
                            })}{" "}
                            {formatEpochDateTime(stage.updatedAt)}
                          </Typography>
                        )}
                        {stage.endedAt && (
                          <Typography variant="caption" display="block">
                            {t("settings:redactionPlanStageEndedAt", {
                              defaultValue: "Ended at",
                            })}{" "}
                            {formatEpochDateTime(stage.endedAt)}
                          </Typography>
                        )}
                      </StepContent>
                    </Step>
                  );
                })}
              </Stepper>
            </Box>
          ) : null}
          {!isLoading && !error && !plan?.stages?.length && (
            <Typography variant="body2" sx={{ mt: 2 }}>
              {t("common:noData")}
            </Typography>
          )}
        </Box>
      </DialogContentWrapper>
    </DialogComponent>
  );
};
